/**
 * Component Registry for Dietz Property Group Theme
 *
 * Extends the base preferred theme registry with Dietz Property Group specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-preferred/registry';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { ReviewsCallout } from './components/ReviewsCallout';
import { SitemapContent } from './components/SitemapContent';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';

/**
 * Dietz Property Group specific component overrides.
 * These components have been customized for the Dietz Property Group brand.
 *
 * - Footer: style-wrapper override that delegates to the parent and ships
 *   an extra `<style>` block to darken the `.footer-bottom` band so the
 *   `text-white/70` legal nav, compliance icons, and attribution row meet
 *   WCAG AA contrast regardless of the (lighter) Dietz secondary color.
 * - Header: copy-source override that conditionally surfaces the
 *   "Interactive Map" nav item only when the property has a populated
 *   `custom_engrain_sightmap_url` field, and conditionally hides the
 *   "Virtual Tour" nav item when the property has no virtual tours.
 * - Hero: copy-source override that collapses equal-endpoint bedroom
 *   ranges (e.g. "2-2" → "2", "Studio-Studio" → "Studio") in the
 *   subtitle, including the case where an author-customized subtitle
 *   uses a `property_bedrooms_range` field token whose raw value bypasses
 *   the parent's `formatBedroomsLabel` collapse.
 * - ReviewsCallout: wrapper override that returns `null` to suppress
 *   the section everywhere it might be slotted, while keeping the slot
 *   registered (and editable schema intact) for future re-enablement.
 * - SitemapContent: copy-source override that mirrors Header's dynamic
 *   nav rules — hides Virtual Tour and Interactive Map links when the
 *   backing property data (virtual tours / SightMap URL) is missing.
 * - VirtualTourEmbed: wrapper override that delegates to the parent
 *   component and injects `<meta name="robots" content="noindex, nofollow">`
 *   client-side when the property has zero virtual tours, so the empty
 *   Virtual Tour page is excluded from search engine results per-property.
 */
const dietzpropertygroupOverrides: Partial<ComponentRegistry> = {
  Footer,
  Header,
  Hero,
  ReviewsCallout,
  SitemapContent,
  VirtualTourEmbed,
};

/**
 * Final merged registry for Dietz Property Group properties.
 * Inherits all components from the preferred theme, with Dietz Property Group overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...dietzpropertygroupOverrides,
};

export default registry;
